import {
  Box,
  Callout,
  DebtRepaymentProjection,
  DebtRepaymentSuggestion,
  Disclaimer,
  Div,
  H5,
  P,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDay } from '../helpers/receivablesLabels';
import {
  durationLabel,
  projectionMethods,
  sameMethods,
} from '../helpers/repaymentSuggestionLabels';
import { BulletList, FigureRow } from './RepaymentSuggestion.styled';

type Props = {
  data: DebtRepaymentSuggestion;
  /** The AI option on show, compared with the rest. */
  projection: DebtRepaymentProjection;
  /** Its column's heading: "AI suggestion", "Option A", "Option B". */
  title: string;
  /** The AI's words on the difference; only option A has them. */
  comparison?: string;
};

type Row = {
  label: string;
  cell: (projection: DebtRepaymentProjection) => string;
};

type Column = {
  title: string;
  projection: DebtRepaymentProjection;
  warnings: string[];
};

/**
 * The columns worth comparing: the AI's, the admin's own values from the
 * form, the active plan. An edit opens with the form filled from the active
 * plan, so until something is changed the admin's values ARE the plan: one
 * column then, carrying the warnings, instead of two identical ones.
 */
const columnsFor = (
  { scenario, scenarioWarnings, currentPlan }: DebtRepaymentSuggestion,
  suggestion: DebtRepaymentProjection,
  title: string
): Column[] => {
  const scenarioIsPlan =
    !!scenario && !!currentPlan && sameMethods(scenario, currentPlan);
  return [
    { title, projection: suggestion, warnings: [] },
    ...(scenario && !scenarioIsPlan
      ? [
          {
            title: 'Your values',
            projection: scenario,
            warnings: scenarioWarnings,
          },
        ]
      : []),
    ...(currentPlan
      ? [
          {
            title: 'Active plan',
            projection: currentPlan,
            warnings: scenarioIsPlan ? scenarioWarnings : [],
          },
        ]
      : []),
  ];
};

/**
 * The AI's plan next to the admin's values and / or the active plan, with the
 * AI's words on the difference. Only there when there is something to
 * compare with.
 */
export const SuggestionComparison = ({
  data,
  projection,
  title,
  comparison,
}: Props) => {
  const columns = columnsFor(data, projection, title);
  if (columns.length < 2) return null;

  const rows: Row[] = [
    // The methods by position: projectionMethods always lists the same three.
    ...projectionMethods(projection).map(({ label }, index) => ({
      label,
      cell: (p: DebtRepaymentProjection) => projectionMethods(p)[index].value,
    })),
    { label: 'Per month', cell: (p) => displayCentsInDollars(p.monthlyTotal) },
    { label: 'Repaid in', cell: (p) => durationLabel(p) ?? 'Never' },
    {
      label: 'Done by',
      cell: (p) =>
        p.estimatedCompletionDate ? formatDay(p.estimatedCompletionDate) : '-',
    },
    ...(data.targetMonths != null
      ? [
          {
            label: 'Target',
            cell: (p: DebtRepaymentProjection) =>
              p.meetsTarget ? 'Meets' : 'Misses',
          },
        ]
      : []),
  ];
  const warned = columns.filter((column) => column.warnings.length > 0);

  return (
    <Div $flex $column $gap="12px">
      <H5 $m="0">Compared</H5>
      <Box>
        <FigureRow $columns={columns.length}>
          <span />
          {columns.map((column) => (
            <Disclaimer key={column.title} $m="0" $color={lightTheme.lightGray}>
              {column.title}
            </Disclaimer>
          ))}
        </FigureRow>
        {rows.map((row) => (
          <FigureRow key={row.label} $columns={columns.length}>
            <P $m="0">{row.label}</P>
            {columns.map((column, index) => (
              // The AI's column is the one being proposed, so it stands out.
              <P key={column.title} $m="0" $bold={index === 0}>
                {row.cell(column.projection)}
              </P>
            ))}
          </FigureRow>
        ))}
      </Box>

      {warned.map((column) => (
        <Callout key={column.title} tone="warning">
          <P $m="0 0 4px" $bold>
            {column.title}
          </P>
          <BulletList>
            {column.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </BulletList>
        </Callout>
      ))}

      {comparison && <P $m="0">{comparison}</P>}
    </Div>
  );
};
